<script setup lang="ts">
import AgentRepoActionsMenu from './AgentRepoActionsMenu.vue'
import StatusPill from '../components/StatusPill.vue'
import { repoContainerBadge, repoProductOwnerLabel, repoSessionBadge } from './AgentRepoStates'
import type { AgentRepoStatus } from '../stores/agent_repos'

// The Repos page's table: one row per registered repo with its source and
// checkout branch, devcontainer state, product-owner state and agent-session
// state, plus the row's ··· menu (AgentRepoActionsMenu). Picking a row selects
// it for the detail panel beside the table. Wide grid inside an overflow-x
// box, so a phone scrolls the table rather than the page.
defineProps<{
  repos: AgentRepoStatus[]
  selectedSlug: string | null
  // Briefly ringed by a `?repo=` deep-link from a session (#1362).
  highlightedSlug: string | null
}>()

const emit = defineEmits<{ select: [slug: string] }>()

const GRID = 'grid grid-cols-[2fr_1.3fr_1.4fr_1fr_40px] items-center gap-3 px-4'
</script>

<template>
  <div class="overflow-x-auto rounded-2xl border border-border-subtle">
    <div class="min-w-[640px]">
      <div
        :class="GRID"
        class="bg-surface-rail py-2.5 text-[11px] font-semibold tracking-[0.08em] text-ink-faint uppercase"
        aria-hidden="true"
      >
        <span>Repository</span><span>Devcontainer</span><span>Product owner</span><span>Agent session</span><span />
      </div>
      <ul class="m-0 list-none p-0" data-testid="agent-repo-list">
        <li
          v-for="repo in repos"
          :key="repo.slug"
          :class="[
            GRID,
            repo.slug === selectedSlug ? 'bg-surface-raised shadow-[inset_2px_0_0_var(--color-factory)]' : '',
            repo.slug === highlightedSlug ? 'ring-2 ring-factory/60 ring-inset' : '',
          ]"
          class="min-h-15 cursor-pointer border-t border-border-divider py-2 transition-shadow hover:bg-surface-raised"
          :data-testid="`agent-repo-${repo.slug}`"
          @click="emit('select', repo.slug)"
        >
          <button
            type="button"
            class="flex min-w-0 flex-col gap-0.5 rounded-md py-1 text-left"
            :aria-current="repo.slug === selectedSlug ? 'true' : undefined"
            aria-controls="agent-repo-detail"
            :data-testid="`agent-repo-select-${repo.slug}`"
            @click.stop="emit('select', repo.slug)"
          >
            <span class="flex min-w-0 items-center gap-2">
              <span class="truncate font-mono text-[13px] text-ink">{{ repo.slug }}</span>
              <StatusPill
                v-if="repo.is_default"
                label="Default"
                color-class="bg-surface-hover text-ink-muted"
                :data-testid="`agent-repo-default-${repo.slug}`"
              />
            </span>
            <span class="truncate text-xs text-ink-faint">
              {{ repo.git_branch ?? 'branch unknown' }} · from
              <span :data-testid="`agent-repo-source-${repo.slug}`">{{ repo.source === 'db' ? 'DB' : 'YAML' }}</span>
            </span>
          </button>
          <span class="flex items-center gap-2 text-sm" :class="repoContainerBadge(repo).textClass">
            <span class="sr-only">Devcontainer:</span>
            <span class="size-2 shrink-0 rounded-full" :class="repoContainerBadge(repo).dotClass" aria-hidden="true" />
            {{ repoContainerBadge(repo).label }}
          </span>
          <span class="text-sm" :class="repo.product_owner_enabled ? 'text-ink' : 'text-ink-muted'">
            <span class="sr-only">Product owner:</span>
            {{ repoProductOwnerLabel(repo) }}
          </span>
          <span class="flex items-center gap-2 text-sm" :class="repoSessionBadge(repo).textClass">
            <span class="sr-only">Agent session:</span>
            <span class="size-2 shrink-0 rounded-full" :class="repoSessionBadge(repo).dotClass" aria-hidden="true" />
            {{ repoSessionBadge(repo).label }}
          </span>
          <AgentRepoActionsMenu :repo="repo" />
        </li>
      </ul>
    </div>
  </div>
</template>
